import { useEffect, useRef, useState } from 'react';

import { DrawByProductId } from '../../../features/draws/types';
import { useIsSubscribed } from '../../../hooks/subscription';
import { useAuth } from '../../../providers';
import { buildSubscriptions } from '../../../utils/subscriptions';

type Args = {
  draw?: DrawByProductId;
  /** Skips the subscription lookup entirely for the experiment's control arm. */
  enabled: boolean;
  isPreOrder: boolean;
  product: ProductType;
  productRef: string;
};

/**
 * Whether the sticky CTA should stay off the page for this visit.
 *
 * A collector who has already entered the draw or subscribed has nothing left
 * to do from the bar, so it never mounts for them. The answer is settled once,
 * on the first resolved read: entering or subscribing while browsing leaves the
 * bar in place until the page is next loaded (PTE-4262).
 *
 * `settled` is false until that read lands. The ticket runs this check "before
 * anything else", so the bar waits for it rather than painting and vanishing a
 * moment later — a flash is worse than the short wait, and for a logged out
 * visitor there is no wait at all because the lookup is skipped.
 *
 * "Subscribed" matches the main CTA's own success state — every subscription
 * the sign up button would create, not just the product one.
 */
export const useStickyCtaDismissed = ({
  draw,
  enabled,
  isPreOrder,
  product,
  productRef,
}: Args): { dismissed: boolean; settled: boolean } => {
  const { isLoggedIn } = useAuth();

  const subscriptions = buildSubscriptions({
    includeGeneral: true,
    artists:
      product.productArtist?.map((a: ResolvedRelatedArtist) => a.artist) ?? [],
    productId: product.id?.[0]?.id ?? '',
    productRef,
    productTitle: product.title,
    isPreOrder,
  });

  const { isSubscribedAll, loading } = useIsSubscribed(
    enabled
      ? subscriptions.map(({ objectId, type }) => ({ objectId, type }))
      : [],
  );

  const entered = isLoggedIn && Boolean(draw?.accountIsInDraw);

  const [state, setState] = useState({ dismissed: false, settled: false });
  const settled = useRef(false);

  useEffect(() => {
    if (!enabled || settled.current || loading) return;

    settled.current = true;
    setState({ dismissed: entered || isSubscribedAll, settled: true });
  }, [enabled, entered, isSubscribedAll, loading]);

  return state;
};
